/**
 * Luce theme — mass-spring cloth/wave fragment shader.
 *
 * ATTRIBUTION: in the spirit of Amanda Ghassaei's MassSpringShader
 * (https://github.com/amandaghassaei/MassSpringShader, MIT) — the
 * propagating-wave-on-a-springy-sheet look. This is ORIGINAL GLSL,
 * not a port: the reference runs a stateful mass-spring lattice on
 * ping-pong textures; this renders the same visual character as a
 * STATELESS analytic superposition of traveling waves (a closed-form
 * solution of the same wave equation the lattice integrates), which
 * fits the repo's single-pass glContext pipeline with zero
 * framebuffer state. Nothing scraped or hotlinked; technique
 * reimplemented under the MIT terms with this attribution.
 *
 * Visual: golden woven silk (Luce's instrument-panel gold) rolling
 * in slow waves. Lit with a lambert + Blinn sheen so the wave crests
 * catch light like fabric.
 *
 * Interaction + charge wiring:
 *   - `uIntensity` (powerKw / 50) breathes the wave amplitude and
 *     the sheen strength — the cloth visibly pulses WITH the charge
 *     and settles when power tapers.
 *   - `uKwhScale` drives the canonical two-term wind-up (desaturate
 *     + darken at session start, vibrant at full) and grows the
 *     baseline swell as energy accumulates.
 *   - `uScatter` (tap impulse, decays in the component tick) injects
 *     a radial ripple that BEGINS AT THE TOUCH POINT (uRippleOrigin,
 *     in the shader's centered p-space) and expands outward behind a
 *     travelling wavefront (uRippleAge seconds since the tap) — touch
 *     the cloth anywhere and the rings radiate from your finger, the
 *     reference app's signature. Origin defaults to the center when
 *     nothing has been touched.
 *   - `uSpin` (swipe) sways the light direction so a drag tilts the
 *     sheen across the fabric.
 *
 * Cache key: 'cloth:cloth-dark' / 'cloth:cloth-light'. Palette is
 * uniform-injected per variant; the CLOTH_VARIANT define is declared
 * for forward-compat source branching, unused in the body today.
 *
 * Performance: 4 sine waves + 1 ripple evaluated 3× per pixel (value
 * + two finite-difference taps for the normal) — ~15 sin() per
 * pixel, far below cyber's 150-iteration loop. Runs under the 30fps
 * sim cap (HEAVY_SIM_KINDS) regardless.
 */
export const CLOTH_SHADER_SOURCE = `#version 300 es
precision highp float;
#define CLOTH_DARK  0
#define CLOTH_LIGHT 1
// CLOTH_VARIANT injected via prepend:
// #define CLOTH_VARIANT CLOTH_DARK
uniform float iTime;
uniform vec2 iResolution;
uniform float uIntensity;
uniform float uKwhScale;
// Tap ripple impulse (0 at rest; decays in the component tick).
uniform float uScatter;
// Where the ripple radiates from, in this shader's centered p-space
// (the component converts the tap's canvas position). (0,0) = center.
uniform vec2 uRippleOrigin;
// Seconds since the tap — drives the expanding wavefront so the rings
// visibly BEGIN at the touch point and travel outward.
uniform float uRippleAge;
// Swipe sway (accumulated yaw/pitch radians from the drag handlers).
uniform vec2 uSpin;
uniform vec3 uClothColor;
uniform vec3 uSheenColor;
uniform vec3 uShadowColor;
uniform vec3 uBgColor;
out vec4 fragColor;

// Analytic height field: a superposition of traveling waves (the
// closed-form character of a mass-spring sheet), plus the tap ripple
// radiating from the TOUCH POINT with distance falloff. The rings
// only exist behind an expanding wavefront (front = age * speed), so
// the disturbance visibly starts at the finger and travels outward
// instead of appearing everywhere at once.
float height(vec2 p, float t, float amp, float ripple) {
  float h = 0.0;
  h += sin(p.x * 6.0 + t * 1.2) * 0.35;
  h += sin(p.x * 3.4 - p.y * 5.2 - t * 0.9) * 0.28;
  h += sin(p.x * 9.1 + p.y * 7.3 + t * 1.7) * 0.16;
  h += sin(p.y * 4.2 - t * 0.6) * 0.21;
  float d = length(p - uRippleOrigin);
  float front = uRippleAge * 1.6;
  float behindFront = smoothstep(front + 0.12, front - 0.55, d);
  h += ripple * sin(26.0 * d - uRippleAge * 9.0) * exp(-2.2 * d) * behindFront * 1.4;
  return h * amp;
}

void main() {
  vec2 uv = gl_FragCoord.xy / iResolution.xy;
  float aspect = iResolution.x / iResolution.y;
  vec2 p = (uv - 0.5) * vec2(aspect, 1.0) * 2.0;
  float t = iTime;

  // Charge pulse: instantaneous power breathes the swell. The slow
  // 1.4 rad/s pulse keeps it organic rather than metronomic; kWh
  // accumulation raises the baseline so a long session rolls higher.
  float pulse = 0.75 + 0.25 * sin(t * 1.4);
  float amp = (0.35 + 0.65 * uKwhScale) * (0.45 + 0.9 * uIntensity * pulse);

  float h = height(p, t, amp, uScatter);
  // Normal from finite differences of the height field.
  float e = 0.012;
  float hx = height(p + vec2(e, 0.0), t, amp, uScatter) - h;
  float hy = height(p + vec2(0.0, e), t, amp, uScatter) - h;
  vec3 n = normalize(vec3(-hx / e, -hy / e, 1.0));

  // Light swayed by the swipe: dragging tilts the sheen across the
  // fabric. sin() keeps the sway bounded however far the spin winds.
  vec3 l = normalize(vec3(0.35 + 0.30 * sin(uSpin.x), 0.50 + 0.30 * sin(uSpin.y), 0.80));
  float diff = clamp(dot(n, l), 0.0, 1.0);
  vec3 hv = normalize(l + vec3(0.0, 0.0, 1.0));
  float spec = pow(clamp(dot(n, hv), 0.0, 1.0), 24.0);

  // Fine thread weave — subtle crosshatch so the surface reads as
  // fabric, not liquid.
  float weave = 0.92 + 0.08 * sin(p.x * 140.0) * sin(p.y * 140.0);

  vec3 base = mix(uShadowColor, uClothColor, 0.30 + 0.70 * diff);
  vec3 col = base * weave + uSheenColor * spec * (0.5 + 0.8 * uIntensity);

  // Soft vignette into the theme bg so the sheet sits in the frame.
  // (1.0 - smoothstep) rather than reversed edges — smoothstep with
  // edge0 > edge1 is undefined behavior per the GLSL spec.
  float vig = 1.0 - smoothstep(0.55, 1.35, length(p));
  col = mix(uBgColor, col, vig);

  // Wind-up with a GOLDEN floor (Luce): the cloth still comes to life as
  // energy accumulates, but the resting look is the golden silk from the
  // preview rather than the near-black grey the canonical (0.05 / uKwhScale)
  // wind-up produced on a real session at low kWh. Saturation floors at 70%
  // and brightness at 60%, so even at 0 kWh Luce reads gold.
  float luminance = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(luminance), col, 0.70 + 0.30 * uKwhScale);
  col *= 0.60 + 0.40 * uKwhScale;

  fragColor = vec4(col, 1.0);
}`;

/** Cloth shader variant names — the Theme.shaderVariant axis for Luce. */
export type ClothVariant = 'cloth-dark' | 'cloth-light';

/** Numeric values for the `#define CLOTH_VARIANT` injection (declared for
 *  forward-compat source branching; unused in the body today). */
export const CLOTH_VARIANT_CONSTANTS: Record<ClothVariant, number> = {
  'cloth-dark': 0,
  'cloth-light': 1,
};

/**
 * Per-variant uniform tuples, linear-RGB [0,1] floats, authored from Luce's
 * theme tokens (packages/skins luce/variants.ts):
 *   dark:  accent/charging gold #F5D547 on the near-black panel.
 *   light: deep gold #B8860B (light-mode accent) on warm cream.
 */
export const CLOTH_UNIFORMS = {
  'cloth-dark': {
    uClothColor: [0.96, 0.835, 0.28],
    uSheenColor: [1.0, 0.94, 0.62],
    uShadowColor: [0.16, 0.12, 0.03],
    uBgColor: [0.04, 0.035, 0.02],
  },
  'cloth-light': {
    uClothColor: [0.72, 0.53, 0.04],
    uSheenColor: [0.9, 0.72, 0.2],
    uShadowColor: [0.85, 0.8, 0.66],
    uBgColor: [0.96, 0.94, 0.88],
  },
} as const satisfies Record<ClothVariant, {
  uClothColor: readonly [number, number, number];
  uSheenColor: readonly [number, number, number];
  uShadowColor: readonly [number, number, number];
  uBgColor: readonly [number, number, number];
}>;

/**
 * Returns the cloth shader source with `#define CLOTH_VARIANT <n>` injected
 * after the `#version` directive. Mirrors prependCyberVariant exactly.
 */
export function prependClothVariant(variant: ClothVariant): string {
  const kind = CLOTH_VARIANT_CONSTANTS[variant];
  const lines = CLOTH_SHADER_SOURCE.split('\n');
  const versionIdx = lines.findIndex((line) => line.startsWith('#version'));
  if (versionIdx === -1) return CLOTH_SHADER_SOURCE;
  const before = lines.slice(0, versionIdx + 1);
  const after = lines.slice(versionIdx + 1);
  const inject = `#define CLOTH_VARIANT ${kind}`;
  return [...before, inject, ...after].join('\n');
}
